import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, EMPTY, throwError } from 'rxjs';
import { finalize, catchError, switchMap, filter, take, tap } from 'rxjs/operators';
import { AccountService } from './account.service';
import { SharedService } from './shared.service';
import { MessageService } from 'primeng/api';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  constructor(
    private accountService: AccountService,
    private sharedService: SharedService,
    private messageService: MessageService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.sharedService.showLoader();

    if (request.body instanceof FormData) {
      request = request.clone({ withCredentials: true });
    } else {
      request = request.clone({
        setHeaders: { 'Content-Type': 'application/json' },
        withCredentials: true
      });
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !request.url.includes('api/account/refresh') && !request.url.includes('api/account/login')) {
          return this.handle401Error(request, next);
        }
        if (err.status === 403 && !request.url.includes('api/account/refresh')) {
          const msg = err.error?.message || err.error?.Message;
          this.messageService.add({
            severity: 'warn',
            detail: msg || 'You do not have permission to perform this action.',
            closable: false,
            life: 10000
          });
          this.sharedService.emitForbidden();
          return EMPTY;
        }
        return throwError(() => err);
      }),
      finalize(() => this.sharedService.hideLoader())
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.accountService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          if (response && !response.isError) {
            this.refreshTokenSubject.next(true);
            return next.handle(request);
          } else {
            this.handleRefreshFailure('Session expired. Please log in again.');
            return EMPTY;
          }
        }),
        catchError((err: HttpErrorResponse) => {
          const msg = err.status === 403
            ? 'Your session has expired. Please log in again.'
            : 'Authentication failed. Please log in again.';
          this.handleRefreshFailure(msg);
          return EMPTY;
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((success) => success ? next.handle(request) : EMPTY)
      );
    }
  }

  private handleRefreshFailure(message?: string): void {
    this.isRefreshing = false;
    this.refreshTokenSubject.next(false);
    if (message) {
      this.messageService.add({
        severity: 'warn',
        detail: message,
        closable: false,
        life: 10000
      });
    }
    this.accountService.clearSessionData();
  }
}